// SelloRecepcionBox — "Sello de Recepción" de una OC (Iter 411).
// Muestra estado (Pendiente / Parcial / Recibido), quién recibió, fecha, avance
// y observaciones de recepción. Si `editable`, permite editar las observaciones
// (PUT /bodega/oc/{numero}/sello-recepcion). Reutilizado por OCViewer (vista 1:1
// del PDF) y por la pantalla de Recepción de OC.
import { useState, useEffect } from 'react';
import { PackageCheck, Clock, PackageOpen, Pencil, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import { fmtFecha } from '../lib/utils';
import api from '../lib/api';

const ESTADO_STYLES = {
  recibido: {
    chip: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    box: 'border-emerald-300 bg-emerald-50/60',
    title: 'text-emerald-800',
    icon: PackageCheck,
    label: 'Recibido',
  },
  parcial: {
    chip: 'bg-blue-100 text-blue-700 border-blue-300',
    box: 'border-blue-300 bg-blue-50/60',
    title: 'text-blue-800',
    icon: PackageOpen,
    label: 'Parcial',
  },
  pendiente: {
    chip: 'bg-amber-100 text-amber-800 border-amber-300',
    box: 'border-amber-300 bg-amber-50/60',
    title: 'text-amber-800',
    icon: Clock,
    label: 'Pendiente',
  },
};

export function SelloRecepcionBox({ sello, editable = false, numeroOc, onUpdated, variant = 'viewer' }) {
  const [editing, setEditing] = useState(false);
  const [obs, setObs] = useState(sello?.observaciones || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setObs(sello?.observaciones || ''); }, [sello?.observaciones]);

  if (!sello) return null;

  const estado = (sello.estado || 'pendiente').toLowerCase();
  const st = ESTADO_STYLES[estado] || ESTADO_STYLES.pendiente;
  const Icon = st.icon;
  const isViewer = variant === 'viewer';

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/bodega/oc/${numeroOc}/sello-recepcion`, { observaciones: obs });
      onUpdated?.(res.data);
      setEditing(false);
      toast.success('Observaciones de recepción guardadas');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudieron guardar las observaciones');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`rounded border ${st.box} ${isViewer ? 'mx-3 my-2 px-3 py-2' : 'p-4'}`}
      data-testid="sello-recepcion"
    >
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-2">
          <Icon className={`${isViewer ? 'w-3.5 h-3.5' : 'w-4 h-4'} ${st.title}`} />
          <span className={`font-bold uppercase tracking-wide ${isViewer ? 'text-[9px]' : 'text-xs'} ${st.title}`}>
            Sello de Recepción
          </span>
          <span
            className={`inline-flex items-center rounded-full border px-2 py-0.5 font-bold ${isViewer ? 'text-[8px]' : 'text-[10px]'} ${st.chip}`}
            data-testid="sello-estado"
          >
            {sello.estado_label || st.label}
          </span>
        </div>
        {editable && !editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="inline-flex items-center gap-1 text-[10px] font-medium text-gray-600 hover:text-gray-900"
            data-testid="sello-editar-btn"
          >
            <Pencil className="w-3 h-3" /> Editar
          </button>
        )}
      </div>

      <div className={`grid grid-cols-3 gap-2 ${isViewer ? 'text-[9px]' : 'text-xs'}`}>
        <div>
          <span className="font-semibold text-gray-500">Recibido por: </span>
          <span className="text-gray-800" data-testid="sello-recibido-por">{sello.recibido_por || '—'}</span>
        </div>
        <div>
          <span className="font-semibold text-gray-500">Fecha: </span>
          <span className="text-gray-800" data-testid="sello-fecha">
            {sello.fecha_recepcion ? fmtFecha(sello.fecha_recepcion) : '—'}
          </span>
        </div>
        <div>
          <span className="font-semibold text-gray-500">Avance: </span>
          <span className="text-gray-800" data-testid="sello-avance">
            {sello.items_completos}/{sello.total_items} ítems
          </span>
        </div>
      </div>

      <div className="mt-1.5">
        {editing ? (
          <div className="space-y-1.5">
            <textarea
              value={obs}
              onChange={(e) => setObs(e.target.value)}
              rows={2}
              maxLength={500}
              className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
              placeholder="Observaciones de recepción (ej: entrega parcial, faltó 1 bulto, material con daño...)"
              data-testid="sello-obs-input"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="inline-flex items-center gap-1 rounded bg-emerald-600 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
                data-testid="sello-guardar-btn"
              >
                <Save className="w-3 h-3" /> {saving ? 'Guardando…' : 'Guardar'}
              </button>
              <button
                type="button"
                onClick={() => { setEditing(false); setObs(sello.observaciones || ''); }}
                className="inline-flex items-center gap-1 rounded border border-gray-300 px-2.5 py-1 text-[11px] font-medium text-gray-600 hover:bg-gray-50"
                data-testid="sello-cancelar-btn"
              >
                <X className="w-3 h-3" /> Cancelar
              </button>
            </div>
          </div>
        ) : (
          <p className={`${isViewer ? 'text-[9px]' : 'text-xs'} text-gray-700`} data-testid="sello-observaciones">
            <span className="font-semibold text-gray-500">Observaciones: </span>
            {sello.observaciones ? sello.observaciones : <span className="italic text-gray-400">—</span>}
          </p>
        )}
      </div>
    </div>
  );
}
